/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import useReloadRedirect from "../../hooks/useReloadRedirect";
import useViewportWidth from "../../hooks/useViewportWidth";
import { Layout, Modal, theme, message } from "antd";
import {
  UserOutlined,
  LinkOutlined,
  SolutionOutlined,
  ToolOutlined,
  ReadOutlined,
  TrophyOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import "./ResumeBuilder.css";

import IntroEditor from "../NewResume/IntroEditor";
import SkillsEditor from "../NewResume/SkillsEditor";
import ExperienceEditor from "../NewResume/ExperienceEditor";
import SocialEditor from "../NewResume/SocialEditor";
import EducationEditor from "../NewResume/EducationEditor";
import AchievementsEditor from "../NewResume/AchievementsEditor";
import PaymentStatus from "../NewResume/PaymentStatus";
import {
  authenticatedFetch,
  buildDownloadPurchaseContext,
} from "../NewResume/paymentAccess";
import useSubscriptionStatus from "../Subscription/useSubscriptionStatus";
import TemplateLivePreview from "../NewResume/TemplateLivePreview";
import TemplateRenderer from "../NewResume/templateRenderer";
import {
  getTemplateOptionById,
  getTemplateOptionsForExperienceLevel,
  normalizeExperienceLevel,
} from "../NewResume/templateCatalog";
import {
  logTemplateSelection,
  readTemplateIdFromSearch,
  withTemplateIdInSearch,
} from "../NewResume/templateSelection";
import {
  COLOR_THEME_OPTIONS,
  COLOR_THEME_PRESETS,
} from "../NewResume/colorThemes";

import {
  mapApiResumeToEditor,
  getEmptyResume,
  // shouldShowParseWarning
} from "./mapParsedResumeToState";
import {
  getEmptyLanguageEntries,
  getEmptySkillEntries,
  getIncompleteEducationEntries,
  getIncompleteExperienceEntries,
  getInvalidIntroFields,
  getInvalidSocialFields,
  getMissingRequiredFields,
  getMissingIntroFields,
  getStepValidationError,
  hasAnySocialLink,
  hasFilledEducation,
  hasFilledExperience,
  hasText,
} from "../NewResume/resumeValidation";
import {
  createGeneratedResumeAvatar,
  isGeneratedResumeAvatar,
} from "../NewResume/resumeAvatar";
import { getCompanyNameIssue, getTextSuggestions } from "../NewResume/resumeQuality";

import { useResumeStore } from "../../store/resumeStore";
import { API } from "../../EndpPoints";

const { Content, Sider } = Layout;

export default function ResumeBuilder({
  colorTheme = "blue",
  setColorTheme = () => {},
}) {
  useReloadRedirect({ route: "/resume/new" });
  const location = useLocation();
  const navigate = useNavigate();
  const { useToken } = theme;
  const { token } = useToken();
  const viewportWidth = useViewportWidth();

  const isMobile = viewportWidth <= 768;
  const isTablet = viewportWidth <= 1024;
  const isCompactWorkspace = viewportWidth <= 1024;
  const paymentModalShell = "#ffffff";
  const paymentModalBorder = "1px solid #dfe5ec";
  const paymentModalShadow = "0 28px 80px rgba(15, 23, 42, 0.28)";

  const editorWidth = "35%";
  const previewWidth = "65%";
  const compactPremiumAccent = "#C79A58";
  const compactPremiumAccentGradient =
    "linear-gradient(135deg, #F1D09A 0%, #C79A58 52%, #A8713C 100%)";
  const compactPremiumBlue = "#2F405A";
  const compactPremiumPaper = "#F7F1E8";
  const compactPremiumShell =
    "linear-gradient(180deg, rgba(16, 24, 36, 0.98), rgba(27, 35, 48, 0.95))";

  const workspaceBackground =
    isCompactWorkspace
      ? "radial-gradient(circle at 84% 12%, rgba(185, 135, 75, 0.14), transparent 24%), radial-gradient(circle at 14% 74%, rgba(92, 202, 244, 0.12), transparent 24%), linear-gradient(180deg, #0A0F18 0%, #161C28 54%, #211B1A 100%)"
      : "radial-gradient(circle at 14% 16%, rgba(23, 215, 179, 0.14), transparent 22%), radial-gradient(circle at 84% 12%, rgba(214, 161, 93, 0.12), transparent 24%), linear-gradient(180deg, #07131f 0%, #0d1830 48%, #09111b 100%)";
  const panelBackground =
    "linear-gradient(180deg, rgba(15, 24, 44, 0.94), rgba(17, 26, 47, 0.92))";
  const previewFrameBackground =
    isCompactWorkspace
      ? compactPremiumShell
      : "linear-gradient(180deg, rgba(15, 24, 44, 0.86), rgba(18, 28, 49, 0.82))";
  const panelBorder = isCompactWorkspace
    ? "1px solid rgba(73, 87, 107, 0.62)"
    : "1px solid rgba(76, 102, 136, 0.42)";
  const panelShadow = "0 26px 70px rgba(2, 6, 23, 0.34)";
  const mutedTextColor = "#8ea2bf";
  const colorPresets = COLOR_THEME_PRESETS;
  const colorOptions = COLOR_THEME_OPTIONS;
  const workspaceViewportHeight = isCompactWorkspace ? "100dvh" : "calc(100vh - 60px)";
  const isImportedUpload = sessionStorage.getItem("isUpload") === "yes";
  const selectedExperienceLevel = normalizeExperienceLevel(
    sessionStorage.getItem("experienceLevel"),
  );
  const visibleTemplates = useMemo(
    () =>
      getTemplateOptionsForExperienceLevel(selectedExperienceLevel, {
        smallScreen: isCompactWorkspace,
      }),
    [isCompactWorkspace, selectedExperienceLevel],
  );
  const visibleTemplateIds = useMemo(
    () => visibleTemplates.map((templateOption) => templateOption.id),
    [visibleTemplates],
  );
  const visibleTemplateIdsKey = useMemo(
    () => visibleTemplateIds.join("|"),
    [visibleTemplateIds],
  );
  const routeTemplateId = useMemo(
    () => readTemplateIdFromSearch(location.search) || location.state?.templateId || null,
    [location.search, location.state?.templateId],
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [compactView, setCompactView] = useState("edit");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedDownloadFormat, setSelectedDownloadFormat] = useState("pdf");
  const compactScrollRef = useRef(null);
  const previewTemplateTabsRef = useRef(null);
  const compactDownloadTemplateRef = useRef(null);
  const templateInitSignatureRef = useRef("");
  const [canScrollPreviewTabsLeft, setCanScrollPreviewTabsLeft] = useState(false);
  const [canScrollPreviewTabsRight, setCanScrollPreviewTabsRight] = useState(false);
  const resumeData = useResumeStore((s) => s.resume);
  const updateResume = useResumeStore((s) => s.updateResume);
  const setExportLoading = useResumeStore((s) => s.setExportLoading);
  const selectedTemplateId = useResumeStore((s) => s.selectedTemplateId);
  const selectedTemplateSource = useResumeStore((s) => s.selectedTemplateSource);
  const setSelectedTemplateId = useResumeStore((s) => s.setSelectedTemplateId);
  const effectiveSelectedTemplateId =
    visibleTemplateIds.includes(selectedTemplateId)
      ? selectedTemplateId
      : visibleTemplateIds[0] || "template1";
  const templateInitOptions = useMemo(
    () => ({
      explicitTemplateId: routeTemplateId,
      fallbackTemplateId: visibleTemplateIds[0] || "template1",
      allowedTemplateIds: visibleTemplateIds,
      scope: "/resume/new",
    }),
    [routeTemplateId, visibleTemplateIds],
  );

  const compactPreviewCanvasHeight = useMemo(() => {
    const templateNumber = Number(
      String(effectiveSelectedTemplateId || "").replace("template", ""),
    );
    const isLegacyTemplate = Number.isFinite(templateNumber) && templateNumber <= 24;

    if (isMobile) {
      return isLegacyTemplate ? 4200 : 2800;
    }

    return isLegacyTemplate ? 3600 : 2400;
  }, [effectiveSelectedTemplateId, isMobile]);

  // const showParseWarning = shouldShowParseWarning(resumeData);
  const data = resumeData || getEmptyResume();
  const currentUser = sessionStorage.getItem("userName") || "";
  const { refresh: refreshSubscription } = useSubscriptionStatus();
  const compactDownloadFileName =
    data?.name?.trim().replace(/[\\/:*?"<>|]+/g, " ") || "Resume";
  const compactPaymentContext = useMemo(
    () =>
      buildDownloadPurchaseContext({
        userName: currentUser,
        fileName: compactDownloadFileName,
        format: selectedDownloadFormat,
        downloadLabel: selectedDownloadFormat.toUpperCase(),
        source: "/resume/new",
      }),
    [compactDownloadFileName, currentUser, selectedDownloadFormat],
  );

  const steps = useMemo(
    () => [
      { key: "intro", icon: <UserOutlined />, label: "Profile", hint: "Personal and summary details" },
      { key: "social", icon: <LinkOutlined />, label: "Links", hint: "Social and contact links" },
      { key: "experience", icon: <SolutionOutlined />, label: "Experience", hint: "Career history and impact" },
      { key: "skills", icon: <ToolOutlined />, label: "Skills", hint: "Tools, stack, and strengths" },
      { key: "education", icon: <ReadOutlined />, label: "Education", hint: "Academic and language details" },
      { key: "achievements", icon: <TrophyOutlined />, label: "Wins", hint: "Awards and standout work" },
    ],
    [],
  );

  const updateField = (field, value) => {
    updateResume((prev) => ({
      ...(prev || getEmptyResume()),
      [field]: value,
    }));
  };

  /* ---------------- STEP VALIDATION ---------------- */

  const validateStep = (stepIndex = currentStep) => {
    const resume = resumeData || {};
    const stepKey = steps[stepIndex]?.key;
    const validationError = getStepValidationError(stepKey, resume);

    if (validationError) {
      message.warning(validationError);
      return false;
    }

    return true;
  };

  const moveToStep = (nextStep) => {
    if (nextStep <= currentStep) {
      setCurrentStep(nextStep);
      return;
    }
    for (let stepIndex = currentStep; stepIndex < nextStep; stepIndex += 1) {
      const valid = validateStep(stepIndex);
      if (!valid) {
        setCurrentStep(stepIndex);
        return;
      }
    }
    setCurrentStep(nextStep);
  };

  /* ---------------- IMPORT PARSED DATA ---------------- */

  useEffect(() => {

    if (!location.state?.importedData) return;

    const mapped = mapApiResumeToEditor(location.state.importedData);

    useResumeStore.setState({
      resume: mapped,
    });

    window.history.replaceState({}, document.title);

  }, [location.state]);

  useEffect(() => {
    const initSignature = [
      routeTemplateId || "",
      visibleTemplateIdsKey,
      templateInitOptions.fallbackTemplateId || "",
    ].join("::");

    if (templateInitSignatureRef.current === initSignature) {
      return;
    }

    templateInitSignatureRef.current = initSignature;
    useResumeStore.getState().initializeSelectedTemplate(templateInitOptions);
  }, [
    templateInitOptions,
    routeTemplateId,
    visibleTemplateIdsKey,
  ]);

  useEffect(() => {
    if (!effectiveSelectedTemplateId) return;

    const currentSearchTemplateId = readTemplateIdFromSearch(location.search);
    if (currentSearchTemplateId === effectiveSelectedTemplateId) return;

    navigate(
      {
        pathname: location.pathname,
        search: withTemplateIdInSearch(location.search, effectiveSelectedTemplateId),
      },
      {
        replace: true,
        state: {
          ...(location.state || {}),
          templateId: effectiveSelectedTemplateId,
        },
      },
    );
  }, [
    effectiveSelectedTemplateId,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    logTemplateSelection("/resume/new:route", {
      selectedTemplateId: effectiveSelectedTemplateId,
      source: selectedTemplateSource,
      routeTemplateId,
    });
  }, [effectiveSelectedTemplateId, routeTemplateId, selectedTemplateSource]);

  useEffect(() => {
    const scroller = previewTemplateTabsRef.current;
    if (!scroller || isCompactWorkspace) {
      setCanScrollPreviewTabsLeft(false);
      setCanScrollPreviewTabsRight(false);
      return undefined;
    }

    const updateScrollState = () => {
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      setCanScrollPreviewTabsLeft(scroller.scrollLeft > 4);
      setCanScrollPreviewTabsRight(maxScrollLeft - scroller.scrollLeft > 4);
    };

    updateScrollState();
    scroller.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      scroller.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [effectiveSelectedTemplateId, isCompactWorkspace, visibleTemplates]);

  const activeTemplate = getTemplateOptionById(effectiveSelectedTemplateId);
  const activeStep = steps[currentStep];
  const hasResumeName = hasText(data?.name);
  const generatedAvatar = useMemo(
    () => createGeneratedResumeAvatar(data?.name || ""),
    [data?.name],
  );

  useEffect(() => {
    const currentPhoto = data?.photo || "";
    const photoIsGenerated =
      data?.photoSource === "generated" || isGeneratedResumeAvatar(currentPhoto);

    if (!hasText(data?.name)) {
      if (photoIsGenerated && hasText(currentPhoto)) {
        updateResume({
          photo: "",
          photoSource: "",
        });
      }
      return;
    }

    if (!hasText(currentPhoto) || photoIsGenerated) {
      if (currentPhoto !== generatedAvatar || data?.photoSource !== "generated") {
        updateResume({
          photo: generatedAvatar,
          photoSource: "generated",
        });
      }
    }
  }, [
    data?.name,
    data?.photo,
    data?.photoSource,
    generatedAvatar,
    updateResume,
  ]);

  const reviewItems = useMemo(() => {
    const items = [];
    const missingIntro = getMissingIntroFields(data);
    const invalidIntro = getInvalidIntroFields(data);
    const invalidSocial = getInvalidSocialFields(data);
    const incompleteExperience = getIncompleteExperienceEntries(data);
    const incompleteEducation = getIncompleteEducationEntries(data);
    const emptySkills = getEmptySkillEntries(data);
    const emptyLanguages = getEmptyLanguageEntries(data);
    const summarySuggestion = getTextSuggestions(
      data?.summary || data?.objective || "",
      { limit: 1 },
    )[0];
    const companyIssue = (data?.experience || [])
      .map((experience) => getCompanyNameIssue(experience?.company))
      .find(Boolean);

    if (missingIntro.length > 0) {
      items.push({
        tone: "critical",
        title: "Complete profile basics",
        detail: missingIntro.slice(0, 3).join(", "),
      });
    }

    if (invalidIntro.length > 0) {
      items.push({
        tone: "critical",
        title: "Fix invalid intro values",
        detail: invalidIntro.join(", "),
      });
    }

    if (invalidSocial.length > 0) {
      items.push({
        tone: "critical",
        title: "Fix invalid social links",
        detail: invalidSocial.join(", "),
      });
    } else if (!hasAnySocialLink(data)) {
      items.push({
        tone: "warning",
        title: "Add a social proof link",
        detail: "Include LinkedIn or GitHub for a stronger profile.",
      });
    }

    if (incompleteExperience.length > 0) {
      items.push({
        tone: "critical",
        title: "Finish experience entries",
        detail: `${incompleteExperience.length} role${incompleteExperience.length > 1 ? "s are" : " is"} incomplete.`,
      });
    } else if (!hasFilledExperience(data)) {
      items.push({
        tone: "warning",
        title: "Add at least one experience",
        detail: "A complete role with company, dates, and description is missing.",
      });
    }

    if (emptySkills.length > 0) {
      items.push({
        tone: "warning",
        title: "Clean up skill rows",
        detail: `${emptySkills.length} empty skill slot${emptySkills.length > 1 ? "s" : ""} should be filled or removed.`,
      });
    }

    if (incompleteEducation.length > 0 || !hasFilledEducation(data)) {
      items.push({
        tone: "warning",
        title: "Strengthen education section",
        detail:
          incompleteEducation.length > 0
            ? `${incompleteEducation.length} education entr${incompleteEducation.length > 1 ? "ies are" : "y is"} incomplete.`
            : "Add one complete education entry with degree and duration.",
      });
    }

    if (emptyLanguages.length > 0) {
      items.push({
        tone: "warning",
        title: "Finish language details",
        detail: `${emptyLanguages.length} language row${emptyLanguages.length > 1 ? "s are" : " is"} empty.`,
      });
    }

    if (summarySuggestion) {
      items.push({
        tone: "suggestion",
        title: "Polish wording",
        detail: `Consider "${summarySuggestion.suggestion}" instead of "${summarySuggestion.word}".`,
      });
    }

    if (companyIssue?.suggestion) {
      items.push({
        tone: "suggestion",
        title: "Standardize company name",
        detail: `Use "${companyIssue.suggestion}" for cleaner experience entries.`,
      });
    }

    return items.slice(0, 4);
  }, [data]);

  const reviewSummary = useMemo(() => {
    const criticalCount = reviewItems.filter((item) => item.tone === "critical").length;
    const suggestedCount = reviewItems.length - criticalCount;
    return {
      criticalCount,
      suggestedCount,
      total: reviewItems.length,
    };
  }, [reviewItems]);

  const completedSteps = useMemo(
    () =>
      steps.reduce(
        (count, step) => count + (getStepValidationError(step.key, resumeData || {}) ? 0 : 1),
        0,
      ),
    [resumeData, steps],
  );

  const completionLabel = `${completedSteps}/${steps.length} sections ready`;

  const triggerDownload = useCallback((blob, fileName, extension) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${fileName}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const buildCompactPdfHtml = useCallback((captureNode) => {
    const styleTags = Array.from(document.querySelectorAll("style"))
      .map((styleNode) => styleNode.outerHTML)
      .join("\n");
    const stylesheetLinks = Array.from(
      document.querySelectorAll("link[rel='stylesheet']"),
    )
      .map((linkNode) => linkNode.outerHTML)
      .join("\n");

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    ${stylesheetLinks}
    ${styleTags}
    <style>
      @page { size: A4; margin: 0; }
      html, body { margin: 0; padding: 0; background: #ffffff; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
      body { width: 210mm; }
    </style>
  </head>
  <body>${captureNode.outerHTML}</body>
</html>`;
  }, []);

  const scrollPreviewTabs = (direction) => {
    const scroller = previewTemplateTabsRef.current;
    if (!scroller) return;

    scroller.scrollBy({
      left: direction * 240,
      behavior: "smooth",
    });
  };

  const downloadCompactPdf = useCallback(async () => {
    const sourceNode = compactDownloadTemplateRef.current;
    const fileName = compactDownloadFileName;

    if (!sourceNode) {
      message.error("Preview is not ready. Please try again.");
      setExportLoading(false);
      return {
        ok: false,
        reason: "preview",
        message: "Preview is not ready. Please try again.",
      };
    }

    const captureNode = sourceNode.cloneNode(true);

    captureNode.style.width = "210mm";
    captureNode.style.maxWidth = "210mm";
    captureNode.style.margin = "0";
    captureNode.style.padding = "0";
    captureNode.style.minHeight = "0";
    captureNode.style.height = "auto";
    captureNode.style.background = "#ffffff";

    captureNode
      .querySelectorAll(".cv-page, .page, .cv-content, [data-resume-root='true'], [data-cv-root='true']")
      .forEach((node) => {
        node.style.minHeight = "0";
        node.style.height = "auto";
        node.style.maxHeight = "none";
        node.style.marginBottom = "0";
        node.style.paddingBottom = "0";
        node.style.overflow = "visible";
      });

    try {
      setCompactView("preview");
      const response = await authenticatedFetch(API.GENERATE_PDF, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          html: buildCompactPdfHtml(captureNode),
          resumeData,
          templateId: effectiveSelectedTemplateId,
          fileName,
          scale: 1,
          userName: currentUser,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("Compact backend PDF generation failed:", text);
        message.error("PDF generation failed. Please try again.");
        return {
          ok: false,
          reason: "server",
          message: "PDF generation failed. Please try again.",
          details: text,
        };
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        message.error("Generated PDF is empty.");
        return {
          ok: false,
          reason: "server",
          message: "Generated PDF is empty.",
        };
      }

      triggerDownload(blob, fileName, "pdf");
      message.success("PDF downloaded successfully.");
      return { ok: true };
    } catch (error) {
      console.error("Compact PDF download error:", error);
      return {
        ok: false,
        reason: "client",
        message: "Download failed. Please try again.",
      };
    } finally {
      setExportLoading(false);
    }
  }, [
    buildCompactPdfHtml,
    compactDownloadFileName,
    currentUser,
    effectiveSelectedTemplateId,
    resumeData,
    setExportLoading,
    triggerDownload,
  ]);

  const openCompactDownloadFlow = useCallback(async (format = "pdf") => {
    const missingFields = getMissingRequiredFields(resumeData);

    if (missingFields.length > 0) {
      message.error(
        `Please fix these fields before downloading: ${missingFields.join(", ")}`,
      );
      setExportLoading(false);
      return;
    }

    setSelectedDownloadFormat(format);
    setCompactView("preview");
    try {
      const subscription = await refreshSubscription({ silent: true });
      if (subscription?.active) {
        setExportLoading(true);
        await downloadCompactPdf();
        return;
      }
      if (subscription?.status === "expired") {
        message.warning(
          "Your 7-day subscription has expired. Renew for ₹100 to continue using premium features.",
        );
      }
      setPaymentOpen(true);
    } catch (error) {
      message.error(error.message || "Unable to verify subscription status.");
    } finally {
      setExportLoading(false);
    }
  }, [downloadCompactPdf, refreshSubscription, resumeData, setExportLoading]);

  const handleCompactPaymentSuccess = useCallback(async () => {
    setExportLoading(true);
    return downloadCompactPdf();
  }, [downloadCompactPdf, setExportLoading]);

  useEffect(() => {
    const detail = {
      count: reviewSummary.total,
      isImportedUpload,
    };

    sessionStorage.setItem("cvcanvas:builder-review-count", String(reviewSummary.total));
    window.dispatchEvent(
      new CustomEvent("cvcanvas:builder-review-summary", {
        detail,
      }),
    );
  }, [isImportedUpload, reviewSummary.total]);

  useEffect(() => {
    if (!isCompactWorkspace) return;
    const container = compactScrollRef.current;
    if (!container) return;
    container.scrollTo({ top: 0, behavior: "smooth" });
  }, [compactView, currentStep, isCompactWorkspace]);

  useEffect(() => {
    if (!isCompactWorkspace) return undefined;

    const handleBuilderDownload = (event) => {
      openCompactDownloadFlow(event?.detail?.format || "pdf");
    };

    window.addEventListener("cvcanvas:builder-download", handleBuilderDownload);

    return () => {
      window.removeEventListener(
        "cvcanvas:builder-download",
        handleBuilderDownload,
      );
    };
  }, [isCompactWorkspace, openCompactDownloadFlow]);

  /* ---------------- EDITOR PANEL ---------------- */

  const renderEditorPanel = () => {

    const activeSection = steps[currentStep].key;

    switch (activeSection) {

      case "intro":
        return <IntroEditor data={data} updateField={updateField} />;

      case "social":
        return <SocialEditor data={data} updateField={updateField} />;

      case "experience":
        return <ExperienceEditor data={data} updateField={updateField} />;

      case "skills":
        return (
          <SkillsEditor
            data={data.skillGroups}
            updateSkillGroups={(newGroups) =>
              updateResume((prev) => ({
                ...prev,
                skillGroups: newGroups,
              }))
            }
          />
        );

      case "education":
        return <EducationEditor data={data} updateField={updateField} />;

      case "achievements":
        return <AchievementsEditor data={data} updateField={updateField} />;

      default:
        return null;
    }
  };

  const renderReviewStrip = () => (
    <div
      style={{
        maxWidth: isCompactWorkspace ? "100%" : 980,
        margin: "0 auto",
        padding: isCompactWorkspace ? "14px 16px" : "16px 20px",
        borderRadius: 20,
        background:
          reviewSummary.total > 0
            ? "linear-gradient(180deg, rgba(127, 29, 29, 0.16), rgba(127, 29, 29, 0.08))"
            : "linear-gradient(180deg, rgba(6, 95, 70, 0.18), rgba(6, 95, 70, 0.08))",
        border:
          reviewSummary.total > 0
            ? "1px solid rgba(248, 113, 113, 0.36)"
            : "1px solid rgba(52, 211, 153, 0.32)",
        boxShadow: "0 18px 48px rgba(2, 6, 23, 0.2)",
        color: reviewSummary.total > 0 ? "#fecaca" : "#d1fae5",
      }}
    >
      <div
        style={{
          fontSize: isCompactWorkspace ? 16 : 20,
          fontWeight: 700,
          letterSpacing: "-0.02em",
        }}
      >
        {reviewSummary.total > 0
          ? `AI found ${reviewSummary.total} field${reviewSummary.total > 1 ? "s" : ""} to verify.`
          : "Everything important looks filled in. Keep polishing before preview."}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: isCompactWorkspace ? 13 : 15,
          color: reviewSummary.total > 0 ? "#fee2e2" : "#a7f3d0",
        }}
      >
        {reviewSummary.total > 0
          ? `${reviewSummary.criticalCount} critical, ${reviewSummary.suggestedCount} suggested.`
          : completionLabel}
      </div>
    </div>
  );

  const renderColorPresetPanel = (compact = isCompactWorkspace) => (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "grid",
        gap: 8,
        padding: isMobile ? "14px 16px" : "16px 18px",
        borderRadius: isMobile ? 26 : 28,
        background: compactPremiumShell,
        border: "1px solid rgba(73, 87, 107, 0.72)",
        boxShadow: "0 22px 44px rgba(2, 6, 23, 0.18)",
      }}
    >
      <div
        style={{
          color: "#E6D2B0",
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
          Theme Presets
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {colorOptions.map((color) => {
          const isActive = colorTheme === color;
          return (
            <button
              key={color || "default"}
              type="button"
              className="resume-builder-reset-button"
              onClick={() => setColorTheme(color)}
              style={{
                width: 24,
                minWidth: 24,
                height: 24,
                borderRadius: 999,
                border: isActive
                  ? "2px solid rgba(255,255,255,0.98)"
                  : "1px solid rgba(148, 163, 184, 0.42)",
                background: colorPresets[color].swatch,
                boxShadow: isActive
                  ? `0 0 0 2px ${colorPresets[color].swatch}`
                  : "none",
                flex: "0 0 auto",
                cursor: "pointer",
              }}
              aria-pressed={isActive}
              aria-label={`Select ${color || "default"} theme`}
            />
          );
        })}
      </div>
    </div>
  );

  const renderStartBannerPreview = (compact = false) => (
    <div
      style={{
        width: compact ? "100%" : 794,
        minHeight: compact ? 620 : isTablet ? 520 : 640,
        background: "#f8fafc",
        color: "#152235",
        borderRadius: compact ? 24 : 22,
        overflow: "hidden",
        border: "1px solid rgba(203, 213, 225, 0.92)",
      }}
    >
      <div
        style={{
          padding: compact ? 20 : 28,
          borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
          background: "#ffffff",
          display: "flex",
          alignItems: compact ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: 18,
          flexDirection: compact ? "column" : "row",
        }}
      >
        <div
          style={{
            minWidth: 0,
            maxWidth: compact ? "100%" : 440,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              background: "rgba(37, 99, 235, 0.08)",
              border: "1px solid rgba(37, 99, 235, 0.16)",
              color: "#2563eb",
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            <UserOutlined />
            Resume workspace
          </div>
          <h2
            style={{
              margin: "12px 0 8px",
              fontSize: compact ? 24 : 30,
              lineHeight: 1.12,
              letterSpacing: "-0.035em",
              color: "#0f172a",
            }}
          >
            Add your name to activate the live resume shell.
          </h2>
          <p
            style={{
              margin: 0,
              color: "#64748b",
              fontSize: compact ? 13.5 : 14.5,
              lineHeight: 1.55,
            }}
          >
            Start with the profile fields on the left. The selected template will show your name first, then unlock each section as details are completed.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: compact ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 8,
            width: compact ? "100%" : 270,
          }}
        >
          {[
            { label: "Templates", value: visibleTemplates.length },
            { label: "Mode", value: "ATS" },
            { label: "Step", value: activeStep.label },
          ].map((metric) => (
            <div
              key={metric.label}
              style={{
                minWidth: 0,
                borderRadius: 14,
                padding: "10px 12px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                {metric.label}
              </div>
              <div style={{ marginTop: 5, color: "#0f172a", fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {metric.value}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          padding: compact ? 20 : 28,
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "minmax(0, 1fr) 300px",
          gap: compact ? 18 : 24,
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            borderRadius: 22,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
            padding: compact ? 18 : 22,
            display: "grid",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ color: "#0f172a", fontSize: 17, fontWeight: 850 }}>
                Setup pipeline
              </div>
              <div style={{ marginTop: 4, color: "#64748b", fontSize: 12.5 }}>
                Complete these blocks to build the preview safely.
              </div>
            </div>
            <div
              style={{
                padding: "7px 10px",
                borderRadius: 999,
                background: "#eff6ff",
                color: "#2563eb",
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {completionLabel}
            </div>
          </div>
          {[
            { icon: <UserOutlined />, title: "Profile", text: "Name, title, location, summary" },
            { icon: <LinkOutlined />, title: "Links", text: "LinkedIn, GitHub, portfolio" },
            { icon: <SolutionOutlined />, title: "Experience", text: "Roles, dates, measurable impact" },
            { icon: <ToolOutlined />, title: "Skills", text: "Technical stack and strengths" },
            { icon: <ReadOutlined />, title: "Education", text: "Degree, institute, dates" },
            { icon: <TrophyOutlined />, title: "Wins", text: "Awards, highlights, certifications" },
          ].map((item, index) => (
            <div
              key={item.title}
              style={{
                display: "grid",
                gridTemplateColumns: "34px minmax(0, 1fr)",
                gap: 12,
                alignItems: "center",
                padding: "10px 0",
                borderTop: index === 0 ? "none" : "1px solid #f1f5f9",
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 12,
                  display: "grid",
                  placeItems: "center",
                  background: index === currentStep ? "#eff6ff" : "#f8fafc",
                  color: index === currentStep ? "#2563eb" : "#64748b",
                  border: index === currentStep ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                }}
              >
                {item.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#0f172a", fontSize: 13.5, fontWeight: 800 }}>
                  {item.title}
                </div>
                <div style={{ marginTop: 2, color: "#64748b", fontSize: 12.2, lineHeight: 1.35 }}>
                  {item.text}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            borderRadius: 22,
            background: "linear-gradient(180deg, #ffffff, #f8fafc)",
            border: "1px solid #e2e8f0",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
            padding: compact ? 18 : 20,
            minHeight: 420,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <div style={{ color: "#0f172a", fontSize: 15, fontWeight: 850 }}>
                Resume shell
              </div>
              <div style={{ marginTop: 3, color: "#94a3b8", fontSize: 11.5 }}>
                Waiting for profile name
              </div>
            </div>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b" }} />
          </div>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 18,
              background: "#ffffff",
              padding: 18,
              minHeight: 350,
            }}
          >
            <div style={{ width: "62%", height: 13, borderRadius: 999, background: "#cbd5e1", marginBottom: 8 }} />
            <div style={{ width: "42%", height: 8, borderRadius: 999, background: "#e2e8f0", marginBottom: 22 }} />
            <div style={{ display: "grid", gap: 12 }}>
              {[
                ["Profile summary", "76%"],
                ["Experience", "92%"],
                ["Skills", "64%"],
                ["Education", "82%"],
                ["Achievements", "58%"],
              ].map(([label, width]) => (
                <div key={label}>
                  <div style={{ color: "#334155", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 }}>
                    {label}
                  </div>
                  <div style={{ height: 8, borderRadius: 999, background: "#f1f5f9", overflow: "hidden" }}>
                    <div style={{ width, height: "100%", borderRadius: 999, background: "#bfdbfe" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCompactTemplateChooser = () => (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 8,
            width: "max-content",
            minWidth: "100%",
            padding: "4px 0",
          }}
        >
          {visibleTemplates.map((templateOption) => {
            const isSelected = effectiveSelectedTemplateId === templateOption.id;
            return (
              <button
                key={templateOption.id}
                type="button"
                className="resume-builder-reset-button"
                onClick={() =>
                  setSelectedTemplateId(templateOption.id, "/resume/new:compact-preview")
                }
                style={{
                  width: 54,
                  minWidth: 54,
                  height: 78,
                  padding: 4,
                  borderRadius: 18,
                  border: isSelected
                    ? "2px solid rgba(199, 154, 88, 0.94)"
                    : "1px solid rgba(73, 87, 107, 0.44)",
                  background: isSelected
                    ? "linear-gradient(180deg, rgba(25, 35, 49, 0.98), rgba(34, 44, 58, 0.96))"
                    : "rgba(19, 29, 43, 0.96)",
                  overflow: "hidden",
                  flex: "0 0 auto",
                  contentVisibility: "auto",
                  containIntrinsicSize: "54px 78px",
                  boxShadow: isSelected
                    ? "0 12px 24px rgba(10, 16, 24, 0.24)"
                    : "none",
                }}
                title={templateOption.name}
              >
                <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
                  <TemplateLivePreview
                    templateId={templateOption.id}
                    variant="sidebar"
                    scale={0.058}
                    borderRadius={10}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderPreviewSurface = (compact = false) => (
    <div
      style={{
        background: previewFrameBackground,
        border: panelBorder,
        borderRadius: compact ? 30 : 28,
        padding: compact ? (isMobile ? 16 : 18) : isTablet ? 14 : 18,
        boxShadow: panelShadow,
        backdropFilter: "blur(22px)",
      }}
    >
      {compact ? (
        <div
          style={{
            display: "grid",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                color: "#F8F1E7",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Live Preview
            </div>
            <div
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: "rgba(47, 64, 90, 0.96)",
                color: "#E9EEF7",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              ATS
            </div>
          </div>
          {renderColorPresetPanel(true)}
          {renderCompactTemplateChooser()}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 8,
              maxWidth: "100%",
            }}
          >
            <button
              type="button"
              className="resume-builder-reset-button"
              onClick={() => scrollPreviewTabs(-1)}
              disabled={!canScrollPreviewTabsLeft}
              style={{
                border: "1px solid rgba(76, 102, 136, 0.32)",
                background: canScrollPreviewTabsLeft ? "rgba(17, 26, 47, 0.94)" : "rgba(17, 26, 47, 0.42)",
                color: canScrollPreviewTabsLeft ? "#f8fafc" : "#60728f",
                borderRadius: 14,
                width: 36,
                minWidth: 36,
                height: 36,
                cursor: canScrollPreviewTabsLeft ? "pointer" : "not-allowed",
              }}
            >
              <ArrowLeftOutlined />
            </button>

            <div
              ref={previewTemplateTabsRef}
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                gap: 8,
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {visibleTemplates.map((templateOption) => {
                const isSelected = effectiveSelectedTemplateId === templateOption.id;
                return (
                  <button
                    key={templateOption.id}
                    type="button"
                    className="resume-builder-reset-button"
                    onClick={() =>
                      setSelectedTemplateId(templateOption.id, "/resume/new:desktop-preview")
                    }
                    style={{
                      border: isSelected
                        ? "1px solid rgba(126, 247, 228, 0.54)"
                        : "1px solid rgba(76, 102, 136, 0.28)",
                      background: isSelected
                        ? "linear-gradient(180deg, rgba(16, 35, 58, 0.96), rgba(24, 49, 77, 0.92))"
                        : "rgba(17, 26, 47, 0.72)",
                      color: isSelected ? "#f8fafc" : "#c7d4e6",
                      borderRadius: 0,
                      padding: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      cursor: "pointer",
                      width: 46,
                      height: 64,
                    }}
                    title={templateOption.name}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 54,
                        overflow: "hidden",
                        borderRadius: 0,
                        background: "#ffffff",
                        border: isSelected
                          ? "1px solid rgba(126, 247, 228, 0.32)"
                          : "1px solid rgba(203, 213, 225, 0.22)",
                        contentVisibility: "auto",
                        containIntrinsicSize: "40px 54px",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <TemplateLivePreview
                          templateId={templateOption.id}
                          variant="sidebar"
                          scale={0.045}
                          borderRadius={0}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="resume-builder-reset-button"
              onClick={() => scrollPreviewTabs(1)}
              disabled={!canScrollPreviewTabsRight}
              style={{
                border: "1px solid rgba(76, 102, 136, 0.32)",
                background: canScrollPreviewTabsRight ? "rgba(17, 26, 47, 0.94)" : "rgba(17, 26, 47, 0.42)",
                color: canScrollPreviewTabsRight ? "#f8fafc" : "#60728f",
                borderRadius: 14,
                width: 36,
                minWidth: 36,
                height: 36,
                cursor: canScrollPreviewTabsRight ? "pointer" : "not-allowed",
              }}
            >
              <ArrowRightOutlined />
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          width: compact ? "100%" : "fit-content",
          maxWidth: "100%",
          minHeight: compact ? "auto" : isTablet ? 520 : 640,
          background: "#ffffff",
          border: compact
            ? "1px solid rgba(231, 220, 203, 0.92)"
            : "1px solid rgba(203, 213, 225, 0.22)",
          borderRadius: compact ? 24 : 22,
          overflow: compact ? "auto" : "hidden",
          boxShadow: compact
            ? "0 22px 48px rgba(2, 6, 23, 0.18)"
            : "0 18px 42px rgba(2, 6, 23, 0.18)",
          display: compact ? "block" : "table",
          margin: compact ? 0 : "0 auto",
          padding: 0,
        }}
      >
        {compact ? (
          hasResumeName ? (
            <TemplateLivePreview
              templateId={effectiveSelectedTemplateId}
              fitWidth
              borderRadius={12}
              canvasHeight={compactPreviewCanvasHeight}
            />
          ) : (
            renderStartBannerPreview(true)
          )
        ) : (
          hasResumeName ? (
            <TemplateRenderer templateId={effectiveSelectedTemplateId} />
          ) : (
            renderStartBannerPreview(false)
          )
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`resume-builder-workspace${isCompactWorkspace ? " resume-builder-workspace--compact" : ""}`}
      style={{
        height: workspaceViewportHeight,
        width: "100%",
        maxWidth: "100%",
        background: workspaceBackground,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Modal
        open={paymentOpen}
        footer={null}
        onCancel={() => {
          setPaymentOpen(false);
          setExportLoading(false);
        }}
        centered
        width={isMobile ? "calc(100vw - 24px)" : 620}
        className="payment-modal-panel"
        wrapClassName={isMobile ? "payment-modal-wrap payment-modal-wrap--mobile" : "payment-modal-wrap"}
        closeIcon={
          <span style={{ color: "#64748b", fontSize: 22 }}>
            x
          </span>
        }
        styles={{
          mask: {
            backdropFilter: "blur(8px)",
          },
          content: {
            background: paymentModalShell,
            border: paymentModalBorder,
            borderRadius: 12,
            boxShadow: paymentModalShadow,
            padding: isMobile ? "26px 20px 22px" : "32px",
          },
          header: {
            background: "transparent",
          },
          body: {
            background: "transparent",
          },
        }}
      >
        <PaymentStatus
          setPaymentOpen={setPaymentOpen}
          downloadLabel={selectedDownloadFormat.toUpperCase()}
          onPaymentSuccess={handleCompactPaymentSuccess}
          purchaseContext={compactPaymentContext}
        />
      </Modal>
      {!isImportedUpload && !isCompactWorkspace && reviewSummary.total === 0 ? (
        <div
          style={{
            background: workspaceBackground,
            padding: isMobile ? "12px 12px 0" : "0",
            flexShrink: 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          {renderReviewStrip()}
        </div>
      ) : null}

      {isCompactWorkspace ? (
        <div
          ref={compactScrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            padding: isMobile ? 16 : 22,
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: isMobile ? 16 : 20,
            overflowY: "auto",
            overflowX: "hidden",
            alignContent: "start",
            justifyItems: "stretch",
            paddingBottom: isMobile ? 24 : 28,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0,
              padding: 4,
              borderRadius: 24,
              background: compactPremiumShell,
              border: "1px solid rgba(73, 87, 107, 0.72)",
              boxShadow: "0 20px 40px rgba(2, 6, 23, 0.14)",
            }}
          >
            <button
              type="button"
              className="resume-builder-reset-button"
              onClick={() => setCompactView("edit")}
              style={{
                border: "none",
                background: compactView === "edit"
                  ? compactPremiumPaper
                  : "transparent",
                color: compactView === "edit" ? "#1E293B" : "#C3C9D4",
                borderRadius: 18,
                padding: isMobile ? "12px 16px" : "14px 18px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>Edit</div>
            </button>
            <button
              type="button"
              className="resume-builder-reset-button"
              onClick={() => setCompactView("preview")}
              style={{
                border: "none",
                background: compactView === "preview"
                  ? compactPremiumPaper
                  : "transparent",
                color: compactView === "preview" ? "#1E293B" : "#C3C9D4",
                borderRadius: 18,
                padding: isMobile ? "12px 16px" : "14px 18px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>Preview</div>
            </button>
          </div>

          {compactView === "edit" ? (
            <>
              {renderColorPresetPanel(true)}

              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  borderRadius: 30,
                  background: compactPremiumPaper,
                  border: "1px solid rgba(226, 213, 197, 0.92)",
                  boxShadow: "0 18px 40px rgba(2, 6, 23, 0.10)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "auto",
                    overflowY: "visible",
                    padding: "2px",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  {steps.map((step, index) => {
                    const active = index === currentStep;
                    return (
                      <button
                        key={step.key}
                        type="button"
                        className="resume-builder-reset-button resume-builder-step-button"
                        onClick={() => moveToStep(index)}
                        style={{
                          minWidth: isMobile ? 98 : 108,
                          flex: "0 0 auto",
                          height: 48,
                          padding: isMobile ? "0 14px" : "0 18px",
                          borderRadius: 18,
                          border: active
                            ? "1px solid rgba(199, 154, 88, 0.82)"
                            : "1px solid rgba(228, 214, 196, 0.92)",
                          background: active
                            ? "linear-gradient(180deg, rgba(255, 250, 244, 0.98), rgba(247, 240, 229, 0.96))"
                            : "#FFFFFF",
                          color: active ? "#8A6434" : "#7D8A9D",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          style={{
                            fontSize: isMobile ? 11 : 12,
                            lineHeight: 1.1,
                            fontWeight: 700,
                            color: active ? "#8A6434" : "#8A97A9",
                          }}
                        >
                          {step.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  padding: isMobile ? "22px 18px" : "26px 24px",
                  background: compactPremiumPaper,
                  border: "1px solid rgba(226, 213, 197, 0.92)",
                  borderRadius: 34,
                  boxShadow: "0 18px 40px rgba(2, 6, 23, 0.12)",
                  minWidth: 0,
                  overflow: "visible",
                }}
              >
                <div style={{ color: compactPremiumAccent, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                  {activeStep.label}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    color: "#152235",
                    fontSize: 24,
                    fontWeight: 700,
                    fontFamily: "Georgia, 'Times New Roman', serif",
                  }}
                >
                  {activeStep.hint}
                </div>
                <div style={{ marginTop: 16 }}>
                  {renderEditorPanel()}
                </div>
                <div
                  style={{
                    marginTop: 16,
                    height: 34,
                    borderRadius: 14,
                    background: "#1F2A44",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: `${Math.max(16, Math.round((completedSteps / steps.length) * 100))}%`,
                      background: compactPremiumAccentGradient,
                    }}
                  />
                  {/* <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: token.colorTextHeading,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    Tap Preview to open full sheet view
                  </div> */}
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginTop: 2 }}>
                {renderPreviewSurface(true)}
              </div>
            </>
          )}

        </div>
      ) : (
        <Layout
          style={{
            flex: 1,
            minHeight: 0,
            background: workspaceBackground,
            flexDirection: "row",
            gap: 16,
            padding: 18,
            paddingTop: 16,
            overflow: "hidden",
          }}
        >
          <Content
            style={{
              width: editorWidth,
              padding: "22px 20px",
              overflowY: "auto",
              overflowX: "hidden",
              background: panelBackground,
              border: panelBorder,
              display: "flex",
              flexDirection: "column",
              borderRadius: 28,
              backdropFilter: "blur(22px)",
              boxShadow: panelShadow,
              minWidth: 0,
              minHeight: 0,
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              scrollbarGutter: "stable",
              flex: 1,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
                padding: "14px 16px",
                borderRadius: 20,
                background: "rgba(248, 250, 252, 0.96)",
                border: "1px solid rgba(207, 224, 247, 0.8)",
              }}
            >
              <span
                onClick={() => currentStep > 0 && setCurrentStep((p) => p - 1)}
                style={{
                  fontSize: 20,
                  cursor: currentStep === 0 ? "not-allowed" : "pointer",
                  color: currentStep === 0 ? "#cbd5e1" : token.colorPrimary,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ArrowLeftOutlined />
              </span>

              <div
                style={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                {steps.map((step, index) => {
                  const active = index === currentStep;
                  return (
                    <button
                      key={step.key}
                      type="button"
                      className="resume-builder-reset-button resume-builder-step-button"
                      onClick={() => moveToStep(index)}
                      style={{
                        cursor: "pointer",
                        minWidth: 0,
                        width: "100%",
                        minHeight: 72,
                        padding: "8px 6px",
                        borderRadius: 14,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        border: active
                          ? "1px solid rgba(59, 130, 246, 0.16)"
                          : "1px solid transparent",
                        color: active ? token.colorPrimary : mutedTextColor,
                        background: active ? "rgba(59, 130, 246, 0.08)" : "transparent",
                      }}
                    >
                      <div style={{ fontSize: 18, lineHeight: 1 }}>{step.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>{step.label}</div>
                    </button>
                  );
                })}
              </div>

              <span
                onClick={() => {
                  if (currentStep < steps.length - 1) {
                    moveToStep(currentStep + 1);
                  }
                }}
                style={{
                  fontSize: 20,
                  cursor: currentStep === steps.length - 1 ? "not-allowed" : "pointer",
                  color: currentStep === steps.length - 1 ? "#cbd5e1" : token.colorPrimary,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ArrowRightOutlined />
              </span>
            </div>

            <div
              style={{
                marginBottom: 16,
                padding: "16px 18px",
                borderRadius: 22,
                background: "rgba(248, 250, 252, 0.96)",
                border: "1px solid rgba(207, 224, 247, 0.8)",
              }}
            >
              <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "#B9854A" }}>
                {activeStep.label}
              </div>
              <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: "#152235" }}>
                {activeStep.hint}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              {renderEditorPanel()}
            </div>
          </Content>

          <Sider
            width={previewWidth}
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              background: "transparent",
              minWidth: 0,
              minHeight: 0,
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              scrollbarGutter: "stable",
              flex: 1,
            }}
          >
            {renderPreviewSurface(false)}
          </Sider>
        </Layout>
      )}

      {isCompactWorkspace ? (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: "-200vw",
            top: 0,
            width: 794,
            background: "#ffffff",
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <div
            ref={compactDownloadTemplateRef}
            style={{
              width: 794,
              background: "#ffffff",
            }}
          >
            <TemplateRenderer templateId={effectiveSelectedTemplateId} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
